import { isAxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FieldInput, FieldPatternInput } from '.';
import { useIban } from '@workspace/api';

/**
 * Champs IBAN/BIC connectés avec validation API automatique
 * - Récupère automatiquement le BIC à partir de l'IBAN
 * - Validation en temps réel via API
 * - Gestion d'erreurs intégrée
 */
export function IbanFields() {
    const methods = useFormContext();
    const IBAN = methods.watch('DemandeContrat.IBAN');

    const [debouncedIBAN, setDebouncedIBAN] = useState(IBAN);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedIBAN(IBAN), 500);
        return () => clearTimeout(timer);
    }, [IBAN]);

    const queryIBAN = useIban(debouncedIBAN);
    const lastProcessedBIC = useRef<string | null>(null);

    const errorMessage = isAxiosError(queryIBAN.error) ? queryIBAN.error.response?.data?.message : undefined;

    useEffect(() => {
        const detail = queryIBAN.data;
        const newBIC = detail?.bic || '';

        // Ne rien faire si on a déjà traité ce BIC
        if (lastProcessedBIC.current === newBIC) {
            return;
        }

        if (queryIBAN.isSuccess && detail?.bic) {
            methods.setValue('DemandeContrat.BIC', detail.bic);
            methods.clearErrors(['DemandeContrat.IBAN', 'DemandeContrat.BIC']);
            lastProcessedBIC.current = detail.bic;
        } else if (queryIBAN.isError && !queryIBAN.isFetching) {
            methods.setError('DemandeContrat.IBAN', {
                type: 'invalidIBAN',
                message: errorMessage,
            });
            methods.setValue('DemandeContrat.BIC', '');
            lastProcessedBIC.current = '';
        } else if (!queryIBAN.isFetching) {
            methods.clearErrors('DemandeContrat.IBAN');
            methods.setValue('DemandeContrat.BIC', '');
            lastProcessedBIC.current = '';
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryIBAN.isSuccess, queryIBAN.isError, queryIBAN.isFetching, queryIBAN.data?.bic, errorMessage, methods]);


    return (
        <>
            <FieldInput
                label="Nom du titulaire du compte"
                name="DemandeContrat.NomTitulaire"
                placeholder="Nom du titulaire du compte"
                required={true}
                validation={{
                    required: 'Le nom du titulaire est requis',
                }}
            />

            <FieldPatternInput
                label="IBAN"
                loading={queryIBAN.isFetching}
                name="DemandeContrat.IBAN"
                placeholder="Exemple : FR76 3000 1007 9412 3456 7890 185"
                type="iban"
                validation={{
                    validate: {
                        isIBAN: (value) => {
                            if (!value) return true;

                            if (queryIBAN.isError && !queryIBAN.isFetching) {
                                return errorMessage;
                            }

                            return true;
                        },
                    },
                }}
            />

            <FieldInput
                disabled
                label="Code BIC"
                name="DemandeContrat.BIC"
            />
        </>
    );
}
